import React from "react";
import { SnackbarKey, useSnackbar } from "notistack";
import { useQuery, useQueryClient } from "@tanstack/react-query";
// Materials
import { Grid, Typography } from "@mui/material";
// Components
import CommissionCycleAdjustmentTable from "../../tables/PayrollClosing/CommissionCycleAdjustmentTable";
import { SnackbarsActions, SnackbarsContent } from "@/Layouts/components/Snackbars";
import CommissionCycleAdjustmentLoader from "./CommissionCycleAdjustmentLoader";
// Context
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
// API
import { getAdjustments } from "@/Washouts/api/PayrollClosing/queries";
import {
	onCreateAdjustmentCallback,
	onDeleteAdjustmentCallback,
	onUpdateAdjustmentCallback,
} from "@/Washouts/helpers/PayrollClosing/subscriptionCallbacks";
// Types
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";
import { IAdjustment } from "@/Washouts/types/PayrollClosing/subscriptionCallbacks";
import { onCreateAdjustment, onDeleteAdjustment, onUpdateAdjustment } from "@/Washouts/api/PayrollClosing/subscriptions";
import { IStatus } from "@/Common/api/types/CommissionCycle";

type CommissionCycleAdjustmentProps = {
	locationId: string;
	selection: boolean;
	canCreateAdjustments: boolean;
	payPeriodId: string;
	positionStatus: IStatus[];
	onHasAdjustments?: (hasAdjustments: boolean) => void;
};

export default function CommissionCycleAdjustment(props: Readonly<CommissionCycleAdjustmentProps>) {
	const { locationId, selection, canCreateAdjustments, payPeriodId } = props;
	const queryClient = useQueryClient();
	const { enqueueSnackbar } = useSnackbar();

	// Subscriptions
	const { getSubscription, addSubscription } = useSubscriptions();
	const adjustmentSubscription = getSubscription(["payrollClosing", "adjustments", locationId, payPeriodId]);

	adjustmentSubscription?.subscriptions?.onCreateAdjustment?.setCallback(
		(response: GraphqlSubscriptionResponse<"onCreateAdjustment", IAdjustment>) =>
			onCreateAdjustmentCallback({
				queryClient,
				response,
			})
	);

	adjustmentSubscription?.subscriptions?.onDeleteAdjustment?.setCallback(
		(response: GraphqlSubscriptionResponse<"onDeleteAdjustment", IAdjustment>) =>
			onDeleteAdjustmentCallback({
				queryClient,
				response,
			})
	);

	adjustmentSubscription?.subscriptions?.onUpdateAdjustment?.setCallback(
		(response: GraphqlSubscriptionResponse<"onUpdateAdjustment", IAdjustment>) =>
			onUpdateAdjustmentCallback({
				queryClient,
				response,
			})
	);

	const { data: adjustments, isLoading: adjustmentLoading } = useQuery({
		queryKey: ["payrollClosing", "adjustments", locationId, payPeriodId],
		queryFn: async () => {
			try {
				const result = await getAdjustments(locationId, payPeriodId);
				return result?.adjustments ?? [];
			} catch (error) {
				const renderSnackbarsActions = (key: SnackbarKey) => {
					return <SnackbarsActions key={key.toString()} index={key} />;
				};
				enqueueSnackbar(<SnackbarsContent message={"An error occurred retrieving the adjustments."} critical={true} />, {
					variant: "error",
					preventDuplicate: true,
					action: renderSnackbarsActions,
				});
				return [];
			}
		},
		staleTime: Infinity,
		enabled: !!locationId && !!payPeriodId,
	});

	React.useEffect(() => {
		props.onHasAdjustments?.(!!adjustments?.length);
	}, [adjustments]);

	React.useEffect(() => {
		if (!locationId || !payPeriodId) {
			return;
		}

		addSubscription(
			["payrollClosing", "adjustments", locationId, payPeriodId],
			[
				{
					context: "onCreateAdjustment",
					query: onCreateAdjustment,
					variables: {
						locationId,
						payPeriodId,
					},
				},
				{
					context: "onDeleteAdjustment",
					query: onDeleteAdjustment,
					variables: {
						locationId,
						payPeriodId,
					},
				},
				{
					context: "onUpdateAdjustment",
					query: onUpdateAdjustment,
					variables: {
						locationId,
						payPeriodId,
					},
				},
			]
		);
	}, [payPeriodId, locationId]);

	return (
		<React.Fragment>
			<CommissionCycleAdjustmentLoader loading={adjustmentLoading} />

			{selection && !adjustmentLoading && (
				<Grid container>
					<Grid item xs={12}>
						<Typography className="page-title" component="div">
							{"Adjustment"}
						</Typography>
					</Grid>
					<Grid item xs={12} sx={{ minHeight: "100px" }}>
						<CommissionCycleAdjustmentTable
							key={`adjustments-${locationId}-${payPeriodId}`}
							data={adjustments}
							canCreateAdjustments={canCreateAdjustments}
							locationId={locationId}
							payPeriodId={payPeriodId}
							positionStatus={props.positionStatus}
						/>
					</Grid>
				</Grid>
			)}
		</React.Fragment>
	);
}
